import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ChatIntegrationProvider } from '@interfaces/IChatIntegrations';
import { CHAT_INTEGRATION_PROVIDERS } from '@constants/chatIntegrations';

/** A connect that just came back to this page: which app, and whether it failed. */
interface IConnectReturn {
  provider: ChatIntegrationProvider;
  failed: boolean;
}

const isProvider = (value: string | null): value is ChatIntegrationProvider =>
  CHAT_INTEGRATION_PROVIDERS.some((provider) => provider === value);

function readReturn(params: URLSearchParams): IConnectReturn | null {
  const connected = params.get('connected');
  if (isProvider(connected)) return { provider: connected, failed: false };
  const failed = params.get('connectError');
  return isProvider(failed) ? { provider: failed, failed: true } : null;
}

/**
 * Whether this page load is the way back from connecting an app
 * (`?connected=<app>` / `?connectError=<app>`, set by the BFF's callback).
 * Read once, then taken off the address, so a reload does not replay it.
 */
export function useConnectReturn(): IConnectReturn | null {
  const [params, setParams] = useSearchParams();
  const [landed] = useState(() => readReturn(params));

  useEffect(() => {
    if (!landed) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('connected');
        next.delete('connectError');
        return next;
      },
      { replace: true },
    );
  }, [landed, setParams]);

  return landed;
}
